import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Stack,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useCommonGetInit from "../../../events/hooks/useCommonGetInit";
import useProductCategories from "../../visitor/registration/hooks/useProductCategories";

const ExhibitorFilterBar = ({ onSearch, onFilter }) => {
  const { data: Halls } = useCommonGetInit(`user/get-halls-list-1`);
  const { data: countryList } = useCommonGetInit(
    `user/get-exhibitor-country-list`
  );
  const { data: Mdata } = useProductCategories();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedHall, setSelectedHall] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCats, setSelectedSubCats] = useState([]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch?.(value);
  };

  const handleHallChange = (event) => {
    const value = event.target.value;
    setSelectedHall(value);
    onFilter?.({
      hall: value,
      country: selectedCountry,
      cats: selectedSubCats,
      categories: selectedCategories,
    });
  };

  const handleCountryChange = (event) => {
    const value = event.target.value;
    setSelectedCountry(value);
    onFilter?.({
      hall: selectedHall,
      country: value,
      cats: selectedSubCats,
      categories: selectedCategories,
    });
  };

  const handleCategoriesChange = (event, newValue) => {
    setSelectedCategories(newValue);

    const catsValue = Array.isArray(newValue) ? newValue.join(",") : newValue;

    onFilter?.({
      hall: selectedHall,
      country: selectedCountry,
      cats: catsValue,
    });
  };

  const clearHallFilter = () => {
    setSelectedHall("");
    onFilter?.({
      hall: "",
      country: selectedCountry,
      cats: selectedSubCats,
      categories: selectedCategories,
    });
  };

  const clearCountryFilter = () => {
    setSelectedCountry("");
    onFilter?.({
      hall: selectedHall,
      country: "",
      cats: selectedSubCats,
      categories: selectedCategories,
    });
  };
  // ✅ Get unique categories for autocomplete
  const uniqueCategories = useMemo(() => {
    if (!Mdata?.categories) return [];
    return [...new Set(Mdata.categories.map((item) => item.Category))].sort();
  }, [Mdata]);

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="h4" gutterBottom>
        Exhibitors List
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search Exhibitors"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Filter by Hall */}
        <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel>Hall</InputLabel>
          <Select
            label="Hall"
            value={selectedHall}
            onChange={handleHallChange}
            endAdornment={
              selectedHall ? (
                <IconButton size="small" onClick={clearHallFilter}>
                  <ClearIcon />
                </IconButton>
              ) : null
            }
          >
            {Halls?.map((hall) => (
              <MenuItem key={hall.name} value={hall.name}>
                {hall.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filter by Country */}
        <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel>Country</InputLabel>
          <Select
            label="Country"
            value={selectedCountry}
            onChange={handleCountryChange}
            endAdornment={
              selectedCountry ? (
                <IconButton size="small" onClick={clearCountryFilter}>
                  <ClearIcon />
                </IconButton>
              ) : null
            }
          >
            {countryList?.map((country) => (
              <MenuItem key={country.name} value={country.name}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filter by Categories - Multiselect Autocomplete */}
        <Autocomplete
          multiple
          size="small"
          options={uniqueCategories}
          value={selectedCategories}
          onChange={handleCategoriesChange}
          sx={{ minWidth: 300 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Categories"
              placeholder="Select categories"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                size="small"
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
        />
      </Stack>
    </Box>
  );
};

export default ExhibitorFilterBar;
